import React__default, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import styles from './youtube-player.module.pcss.js';
import SvgPlay24 from './play_24.svg.js';
import SvgLoading24 from './loading_24.svg.js';
import { loadExternalScript, waitForYouTubePlayerApi, API_URL } from './utils.js';
var YoutubePlayButtonMode;

(function (YoutubePlayButtonMode) {
  YoutubePlayButtonMode[YoutubePlayButtonMode["White"] = 0] = "White";
  YoutubePlayButtonMode[YoutubePlayButtonMode["Black"] = 1] = "Black";
  YoutubePlayButtonMode[YoutubePlayButtonMode["Primary"] = 2] = "Primary";
})(YoutubePlayButtonMode || (YoutubePlayButtonMode = {}));

var YoutubePlayerMode;

(function (YoutubePlayerMode) {
  YoutubePlayerMode[YoutubePlayerMode["Single"] = 0] = "Single";
  YoutubePlayerMode[YoutubePlayerMode["Playlist"] = 1] = "Playlist";
})(YoutubePlayerMode || (YoutubePlayerMode = {}));

const YoutubePlayer = ({
  id,
  mode = YoutubePlayerMode.Single,
  startTime,
  autoplay = false,
  width,
  height,
  preview,
  previewImgSrc,
  previewImgAlt,
  playButtonMode = YoutubePlayButtonMode.White,
  className
}) => {
  const [showVideo, setShowVideo] = useState(autoplay);
  const [videoLoading, setVideoLoading] = useState(false);
  const [isAPILoaded, setAPILoaded] = useState(false);
  const playerWrapperDivRef = useRef(null);
  const playerDivRef = useRef(null);
  const playerInstanceRef = useRef(null);
  let imgPreview = previewImgSrc;

  if (!previewImgSrc && mode === YoutubePlayerMode.Single) {
    imgPreview = `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;
  }

  const wrapperStyles = {
    width: width,
    height: height
  };
  useEffect(() => {
    if (!showVideo) {
      return;
    }

    const loadYouTubePlayerApi = () => {
      if (isAPILoaded) {
        return Promise.resolve(window.YT);
      }

      return loadExternalScript(API_URL).then(() => waitForYouTubePlayerApi()).then(() => setAPILoaded(true)).then(() => window.YT);
    };

    loadYouTubePlayerApi().then(youtubeApi => {
      if (!playerWrapperDivRef.current || !playerDivRef.current) {
        return;
      }

      const {
        width: videoWidth,
        height: videoHeight
      } = playerWrapperDivRef.current.getBoundingClientRect();
      const options = {
        width: videoWidth,
        height: videoHeight,
        playerVars: {
          autoplay: 1,
          rel: 0,
          start: startTime,
          modestbranding: 1
        },
        events: {
          onReady: () => setVideoLoading(false),
          onStateChange: event => {
            if (event.data === 0) {
              setShowVideo(false);
            }
          }
        }
      };

      if (mode === YoutubePlayerMode.Single) {
        options.videoId = id;
      } else {
        options.playerVars = { ...options.playerVars,
          list: id,
          listType: 'playlist'
        };
      }

      playerInstanceRef.current = new youtubeApi.Player(playerDivRef.current, options);
    });
    return () => {
      if (playerInstanceRef.current) {
        playerInstanceRef.current.destroy();
        playerInstanceRef.current = null;
      }
    };
  }, [isAPILoaded, showVideo, startTime, id, mode]);
  return React__default.createElement("div", {
    ref: playerWrapperDivRef,
    onClick: e => {
      e.preventDefault();
      setShowVideo(true);
      setVideoLoading(true);
    },
    className: classNames(styles.youtubePlayer, className, {
      [styles.showVideo]: showVideo
    }),
    style: wrapperStyles
  }, React__default.createElement("div", {
    className: classNames(styles.preview, {
      [styles.previewPlaylist]: !preview && !imgPreview && mode === YoutubePlayerMode.Playlist
    })
  }, preview ? preview : imgPreview && React__default.createElement("img", {
    src: imgPreview,
    alt: previewImgAlt ? previewImgAlt : '',
    className: styles.imagePreview
  }), React__default.createElement("button", {
    type: "button",
    className: classNames(styles.playButton, {
      [styles.hide]: showVideo && !videoLoading,
      [styles.playButtonWhite]: playButtonMode === YoutubePlayButtonMode.White,
      [styles.playButtonBlack]: playButtonMode === YoutubePlayButtonMode.Black,
      [styles.playButtonPrimary]: playButtonMode === YoutubePlayButtonMode.Primary
    })
  }, videoLoading ? React__default.createElement(SvgLoading24, {
    className: styles.iconLoading
  }) : React__default.createElement(SvgPlay24, null))), React__default.createElement("div", {
    className: styles.player,
    ref: playerDivRef
  }));
};

export { YoutubePlayButtonMode, YoutubePlayer, YoutubePlayerMode };
